/**
 * # Coachmark
 *
 * The announcement callout used to point at one control and say what it does: a filled callout
 * anchored to a target, with a caret. It comes in two sizes. A `card` has a title, a line of copy,
 * and one action, for product tours and first-run hints. A `compact` pill is one bold line, for a
 * short "New!" note next to a control.
 *
 * Pair it with `Spotlight` to dim everything else. The two are separate because the dim is
 * optional — a single hint in a quiet corner of the page rarely needs one.
 *
 * The callout is `background/special` in both themes, so its content uses the `-static` inverse
 * colours rather than the theme-reactive ones.
 *
 * ## Props
 *
 * - `target` (HTMLElement | null): the element the caret points at. Renders nothing while `null`.
 *   Take it with a callback ref held in state, not `useRef` — the component has to re-render when
 *   the element appears, and a ref mutation does not do that.
 * - `size` ('card' | 'compact'): Default `'card'`.
 * - `title` (string): heading, also the dialog's accessible name. Required for a `card`; a
 *   `compact` pill has no heading, so its body names the dialog when the title is left out.
 * - `body` (ReactNode): the copy.
 * - `children` (ReactNode): the action area, right-aligned 12px under a card's copy. Usually one
 *   `Button`. Not rendered in a `compact` pill.
 * - `onDismiss` (() => void): renders a close button, top-right on a card and at the end of a pill.
 * - `placement` (Placement): Popper placement. Default `'bottom'`.
 * - `offset` (number): extra px between the target and the callout, for a target the caret would
 *   otherwise cut into. Default `0`.
 * - `trapFocus` (boolean): hold keyboard focus inside the callout. Default `true`. Turn it off for a
 *   step whose action is the anchored control itself, which the user has to be able to reach.
 * - `className` (string): extra classes for the callout.
 *
 * ## Usage
 *
 * ```jsx
 * const [filterButton, setFilterButton] = useState<HTMLElement | null>(null);
 *
 * <button ref={setFilterButton}>Filter students</button>
 * <Coachmark target={filterButton} title='Filter students' body='Narrow the roster.'>
 *   <Button color='blend-dark' onClick={advance}>Next</Button>
 * </Coachmark>
 *
 * <Coachmark
 *   target={optionsButton}
 *   size='compact'
 *   body='New! Generate suggested grades with AI.'
 *   onDismiss={dismiss}
 *   trapFocus={false}
 * />
 * ```
 */

import React, {useId} from 'react';
import FocusLock from 'react-focus-lock';
import type {Placement} from '@popperjs/core';

import Heading from '../../atoms/Heading/Heading.react';
import Text from '../../atoms/Text/Text.react';
import AnchoredFixedPosition from '../../helpers/AnchoredFixedPosition/AnchoredFixedPosition.react';
import IconButton from '../IconButton/IconButton.react';

// The caret is SCSS, against the Tailwind-only rule: its rotation lives on a pseudo-element and
// its offset keys off `data-placement` on an ancestor. Tailwind renders neither — verified.
import './coachmark.scss';

/**
 * Intercom injects its container as a sibling of `#app` and paints above this card, so without the
 * exemption the lock pulls focus straight back out of its composer. Same predicate atomic `Modal`
 * uses. Absent an `#app` to measure against there is nothing to exempt, so the lock keeps its full
 * reach rather than silently becoming a no-op.
 */
const isInsideApp = (node: HTMLElement) => {
  const appRoot = document.getElementById('app');
  return !appRoot || appRoot.contains(node);
};

/**
 * The card is wider than the scroll containers some anchors live in (a grading panel, a sidebar
 * column), and Popper's default boundary is the trigger's scrollable ancestor — which would squeeze
 * or flip the card inside that container. The viewport is the only sensible boundary here.
 */
const VIEWPORT_BOUNDED = {preventOverflow: {boundariesElement: 'viewport'}} as const;

interface CommonProps {
  target: HTMLElement | null;
  body: React.ReactNode;
  onDismiss?: () => void;
  placement?: Placement;
  offset?: number;
  trapFocus?: boolean;
  className?: string;
  'data-testid'?: string;
}

interface CardProps extends CommonProps {
  size?: 'card';
  title: string;
  children?: React.ReactNode;
}

interface CompactProps extends CommonProps {
  size: 'compact';
  title?: string;
  children?: never;
}

type Props = CardProps | CompactProps;

export const Coachmark = ({
  target,
  size = 'card',
  title,
  body,
  children,
  onDismiss,
  placement = 'bottom',
  offset = 0,
  trapFocus = true,
  className = '',
  'data-testid': testId
}: Props) => {
  const bodyId = useId();

  if (!target) {
    return null;
  }

  const dismissButton = onDismiss ? (
    <IconButton icon='xmark' label='Dismiss' color='blend-dark' size='xs' onClick={onDismiss} />
  ) : null;

  const dialogProps = {
    'aria-label': title,
    'aria-labelledby': title ? undefined : bodyId,
    'aria-modal': trapFocus || undefined,
    'data-testid': testId,
    role: 'dialog'
  };

  const callout =
    size === 'compact' ? (
      <div
        {...dialogProps}
        className={`gap-x0-half background-special elevation-shadow-l3 py-x0-half pl-x1-half pr-x0-half flex items-center rounded-full whitespace-nowrap ${className}`}
      >
        <Text bold color='primary-inverse-static' id={bodyId} size='s'>
          {body}
        </Text>
        {dismissButton}
      </div>
    ) : (
      <div
        {...dialogProps}
        className={`gap-x0-half background-special elevation-shadow-l3 rounded-x1 p-x2 flex w-[255px] flex-col items-end ${className}`}
      >
        <div className='gap-x1 flex w-full items-start'>
          <Heading className='flex-1' color='primary-inverse-static' size='xs'>
            {title}
          </Heading>
          {dismissButton}
        </div>
        <Text className='w-full' color='primary-inverse-static' id={bodyId} size='m'>
          {body}
        </Text>
        {/* Takes the card's 4px gap to 12px, so the action sits further from the copy than the
            copy does from its own heading. */}
        {children ? <div className='mt-x1'>{children}</div> : null}
      </div>
    );

  return (
    <AnchoredFixedPosition
      arrowClassName='m-coachmark-caret'
      className='z-popover'
      modifiers={offset ? {...VIEWPORT_BOUNDED, offset: {offset}} : VIEWPORT_BOUNDED}
      placement={placement}
      target={target}
      usePortal
    >
      {/* The lock wraps the dialog rather than being it: `FocusLock` forwards only its `lockProps`
          to the DOM, so attributes written on it directly are dropped. */}
      {trapFocus ? (
        <FocusLock as='div' whiteList={isInsideApp}>
          {callout}
        </FocusLock>
      ) : (
        callout
      )}
    </AnchoredFixedPosition>
  );
};

export default Coachmark;
